<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
        {{ !form.id ? $t('userAccountCreateModalTitle') : $t('userAccountEditModalTitle') }}
      </div>
    </template>

    <form class="p-fluid" @submit.prevent="saveItem" autocomplete="off">
      <Spinner v-if="dataIsSending"/>

      <div v-else class="p-formgrid p-grid p-pt-3">
        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="user_account_username"
                       v-model.trim="form.username"
                       :maxlength="settings.maxUsernameLength"
                       @blur="onUsernameBlur"
                       @paste="onUsernamePaste"
                       name="user_account_login"
                       autocomplete="new-password"
                       :class="submitted && (
                         !form.username ||
                         form.username.length < settings.minUsernameLength ||
                         form.username.length > settings.maxUsernameLength ||
                         usernameTaken
                       ) ? 'p-invalid' : ''"/>
            <label for="user_account_username">
              {{ $t('Username') }}<span class="warning-color">*</span>
            </label>
          </div>
          <small v-if="submitted && !form.username" class="p-invalid">
            {{ $t('Required field') }}
          </small>
          <small v-else-if="submitted && form.username && form.username.length < settings.minUsernameLength" class="p-invalid">
            {{ $t('tooShortMinChars', { min: settings.minUsernameLength }) }}
          </small>
          <small v-else-if="submitted && form.username &&
                 form.username.length > settings.maxUsernameLength"
                 class="p-invalid">
            {{ $t('maxCharsAllowedMsg', { max: settings.maxUsernameLength }) }}
          </small>
          <small v-else-if="submitted && usernameTaken" class="p-invalid">
            {{ $t('usernameTaken') }}
          </small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="user_account_label"
                       v-model.trim="form.label"
                       autocomplete="off"/>
            <label for="user_account_label">{{ $t('userAccountLabelField') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Password id="user_account_password"
                      v-model="form.password"
                      :disabled="!passwordFieldsEnabled"
                      :feedback="true"
                      toggleMask
                      autocomplete="new-password"
                      :inputClass="submitted && passwordIsRequired && (
                        !form.password ||
                        form.password.length < settings.minPasswordLength ||
                        form.password.length > settings.maxPasswordLength ||
                        passwordServerError
                      ) ? 'p-invalid' : ''"/>
            <label for="user_account_password">
              {{ $t('Password') }}<span v-if="passwordIsRequired" class="warning-color">*</span>
            </label>
          </div>

          <small v-if="submitted && passwordIsRequired && !form.password" class="p-invalid">
            {{ $t('Required field') }}
          </small>

          <small v-else-if="submitted && passwordIsRequired && form.password && form.password.length < settings.minPasswordLength" class="p-invalid">
            {{ $t('tooShortMinChars', { min: settings.minPasswordLength }) }}
          </small>

          <small v-else-if="submitted && passwordIsRequired && form.password &&
                  form.password.length > settings.maxPasswordLength"
                 class="p-invalid">
            {{ $t('maxCharsAllowedMsg', { max: settings.maxPasswordLength }) }}
          </small>

          <small v-else-if="submitted && passwordIsRequired && passwordServerError"
                 class="p-invalid">
            {{ translatePasswordValidationError(passwordServerError) }}
          </small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Password id="user_account_password_repeat"
                      v-model="form.password_repeat"
                      :disabled="!passwordFieldsEnabled"
                      :feedback="false"
                      toggleMask
                      autocomplete="new-password"
                      :inputClass="submitted && passwordIsRequired && (
                        !form.password_repeat ||
                        form.password_repeat !== form.password
                      ) ? 'p-invalid' : ''"/>
            <label for="user_account_password_repeat">
              {{ $t('Repeat password') }}<span v-if="passwordIsRequired" class="warning-color">*</span>
            </label>
          </div>
          <small v-if="submitted && passwordIsRequired && !form.password_repeat" class="p-invalid">
            {{ $t('Required field') }}
          </small>
          <small v-else-if="submitted && passwordIsRequired && form.password_repeat !== form.password" class="p-invalid">
            {{ $t('Passwords do not match') }}
          </small>
        </div>

        <div v-if="form.id"
             class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5"
             style="position: relative; top: -16px;">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="user_account_change_password" v-model="changePassword"/>
            </div>
            <label for="user_account_change_password" class="switch-label pointer">
              {{ $t('changePasswordLabel') }}
            </label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <CustomDropdown id="user_account_default_location"
                            v-model="selectedLocation"
                            :options="locationOptions"
                            :clearSearchData="!visible"
                            optionLabel="public_name"
                            data-key="id"
                            :filter="true"
                            :showClear="true"
                            :class="{ 'p-invalid': submitted && defaultLocationRequired && !selectedLocation }">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <span>{{ slotProps.value.public_name }}</span>
                </div>
                <span v-else style="visibility: hidden">.</span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{ slotProps.option.public_name }}</span>
                </div>
              </template>
            </CustomDropdown>
            <label for="user_account_default_location">
              {{ $t('defaultLocationLabel') }}<span v-if="defaultLocationRequired" class="warning-color">*</span>
            </label>
          </div>
          <small v-if="submitted && defaultLocationRequired && !selectedLocation" class="p-invalid">
            {{ $t('Required field') }}
          </small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <CustomDropdown id="user_account_default_role"
                            v-model="selectedRole"
                            :options="roleOptions"
                            :clearSearchData="!visible"
                            optionLabel="name"
                            data-key="id"
                            :filter="true"
                            :showClear="true"
                            :disabled="!selectedLocation"
                            :class="{ 'p-invalid': submitted && defaultRoleRequired && !selectedRole }">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <span>{{ slotProps.value.name }}</span>
                </div>
                <span v-else style="visibility: hidden">.</span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{ slotProps.option.name }}</span>
                </div>
              </template>
            </CustomDropdown>
            <label for="user_account_default_role">
              {{ $t('defaultRoleLabel') }}<span v-if="defaultRoleRequired" class="warning-color">*</span>
            </label>
          </div>
          <small v-if="submitted && defaultRoleRequired && !selectedRole" class="p-invalid">
            {{ $t('Required field') }}
          </small>
        </div>

        <div class="p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-3 p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="user_account_can_switch_location" v-model="canSwitchLocationBool"/>
            </div>
            <label for="user_account_can_switch_location" class="switch-label pointer">
              {{ $t('canSwitchLocationLabel') }}
            </label>
          </div>
        </div>


        <div class="p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-3 p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="user_account_can_switch_role" v-model="canSwitchRoleBool"/>
            </div>
            <label for="user_account_can_switch_role" class="switch-label pointer">
              {{ $t('canSwitchRoleLabel') }}
            </label>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :loading="disableSaveButton || dataIsSending"
              :disabled="disableSaveButton || dataIsSending || usernameTaken || Boolean(passwordServerError)"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import roles from '@/translations/roles'
import showErrorsMixins from "@/mixins/showErrorsMixins";
import settings from "@/settings";
import constants from "@/constants";
import inputsFormatMixins from '@/mixins/inputsFormatMixins'

export default {
  name: 'UserAccountModal',
  mixins: [showErrorsMixins, inputsFormatMixins],
  emits: ['close', 'update-items'],
  props: {
    item: {
      type: Object,
      default: null,
    },
    visible: Boolean,
    userId: [Number, String],
    locations: {
      type: Array,
      default: () => []
    },
    accessMap: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      submitted: false,
      disableSaveButton: false,
      dataIsSending: false,
      usernameTaken: false,
      passwordServerError: null,
      changePassword: false,
      form: {
        id: null,
        user_id: null,
        username: '',
        label: '',
        password: '',
        password_repeat: '',
        default_location_id: null,
        default_role: null,
        status: 1,
      },
      selectedLocation: null,
      selectedRole: null,
      canSwitchLocationBool: true,
      canSwitchRoleBool: true,
      settings,
    }
  },
  watch: {
    item(value) {
      if (!value) {
        this.resetForm()
        return
      }

      this.form = {
        id: value.id ?? null,
        user_id: value.user_id ?? this.userId ?? null,
        username: value.username ?? '',
        label: value.label ?? '',
        password: '',
        password_repeat: '',
        default_location_id: value.default_location_id ?? null,
        default_role: value.default_role ?? null,
        status: value.status ?? 1,
      }


      if (value.id) {
        this.selectedLocation = this.findLocationById(value.default_location_id)
      } else if (this.locations.length === 1) {
        this.selectedLocation = this.locations[0]
      }

      this.selectedRole = this.findRoleByIdForLocation(value.default_role, value.default_location_id)

      this.canSwitchLocationBool = value.id ? !!value.can_switch_location : true
      this.canSwitchRoleBool = value.id ? !!value.can_switch_role : true

      this.changePassword = !value.id
      this.usernameTaken = false
      this.passwordServerError = null
      this.submitted = false
      this.disableSaveButton = false
      this.dataIsSending = false

      this.$nextTick(() => {
        this.normalizeDefaults()
      })
    },

    visible(value) {
      if (!value) {
        this.submitted = false
        this.disableSaveButton = false
        this.dataIsSending = false
        this.usernameTaken = false
        this.passwordServerError = null

        this.selectedLocation= null
        this.selectedRole= null
      }
    },

    selectedLocation() {
      if (!this.selectedLocation?.id) {
        this.selectedRole = null
        return
      }

      const roleExistsInLocation = this.roleOptions.some(
          item => +item.id === +this.selectedRole?.id
      )

      if (!roleExistsInLocation) {
        this.selectedRole = null
      }
    },

    'form.username'() {
      this.usernameTaken = false

      if (
          this.passwordServerError ===
          'Password must not match username, email or phone number.'
      ) {
        this.passwordServerError = null
      }
    },
    'form.password'() {
      this.passwordServerError = null
    },
    changePassword(value) {
      if (!value) {
        this.form.password = ''
        this.form.password_repeat = ''
        this.passwordServerError = null
      }
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return { position: 'absolute', top: 0, width: '95%', marginTop: '10px' }
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return { position: 'absolute', top: 0, width: '550px', marginTop: '15px' }
      } else {
        return { position: 'absolute', top: 0, width: '550px' }
      }
    },

    locationOptions() {
      return (this.accessMap || []).map(item => ({
        id: +item.location_id,
        public_name: item.public_name || ''
      }))
    },

    roleOptions() {
      if (!this.selectedLocation?.id || !Array.isArray(this.accessMap) || !this.accessMap.length) {
        return []
      }

      const superAdmin = constants.userRoles.superAdmin
      const owner = constants.userRoles.owner
      const admin = constants.userRoles.admin
      const accountant = constants.userRoles.accountant
      const customer = constants.userRoles.customer
      const employeeWorkshopMaster = constants.userRoles.employeeWorkshopMaster

      const selectedAccess = this.accessMap.find(
          item => +item.location_id === +this.selectedLocation.id
      )

      if (!selectedAccess || !Array.isArray(selectedAccess.roles)) {
        return []
      }

      const allowedRoleNums = selectedAccess.roles.map(role => +role)

      return roles
          .filter(role => {
            const roleNum = +role.num

            if (!allowedRoleNums.includes(roleNum)) {
              return false
            }

            if (roleNum === superAdmin || roleNum === customer || roleNum === employeeWorkshopMaster) {
              return false
            }

            if (this.$store.getters.isOwnerOrHigher) {
              return true
            }

            if (this.$store.getters.isAdmin || this.$store.getters.isAccountant) {
              return roleNum !== owner && roleNum !== admin &&  roleNum !== accountant
            }

            return false
          })
          .map(role => ({
            id: +role.num,
            name: role.name?.[this.$i18n.locale] || role.name?.en || String(role.num)
          }))
    },

    defaultLocationRequired() {
      return !this.canSwitchLocationBool || !this.canSwitchRoleBool
    },

    defaultRoleRequired() {
      return !this.canSwitchRoleBool
    },

    passwordFieldsEnabled() {
      return !this.form.id || this.changePassword
    },

    passwordIsRequired() {
      return !this.form.id || this.changePassword
    },
  },
  methods: {
    resetForm() {
      this.form = {
        id: null,
        user_id: this.userId ?? null,
        username: '',
        label: '',
        password: '',
        password_repeat: '',
        default_location_id: null,
        default_role: null,
        status: 1,
      }

      this.selectedLocation = null
      this.selectedRole = null
      this.canSwitchLocationBool = true
      this.canSwitchRoleBool = true
      this.changePassword = true
      this.usernameTaken = false
      this.submitted = false
      this.disableSaveButton = false
      this.dataIsSending = false
      this.passwordServerError = null
    },

    findLocationById(locationId) {
      return this.locationOptions.find(item => +item.id === +locationId) || null
    },

    findRoleByIdForLocation(roleId, locationId = null) {
      const targetLocationId = locationId ?? this.selectedLocation?.id ?? null
      if (!targetLocationId) return null

      const selectedAccess = this.accessMap.find(
          item => +item.location_id === +targetLocationId
      )

      if (!selectedAccess || !Array.isArray(selectedAccess.roles)) {
        return null
      }

      const roleExists = selectedAccess.roles.some(role => +role === +roleId)
      if (!roleExists) return null

      const roleData = roles.find(role => +role.num === +roleId)
      if (!roleData) return null

      return {
        id: +roleData.num,
        name: roleData.name?.[this.$i18n.locale] || roleData.name?.en || String(roleData.num)
      }
    },

    normalizeDefaults() {
      const locationAllowed = this.locationOptions.some(
          item => +item.id === +this.selectedLocation?.id
      )

      if (!locationAllowed) {
        this.selectedLocation = null
        this.selectedRole = null
        return
      }

      const roleAllowed = this.roleOptions.some(
          item => +item.id === +this.selectedRole?.id
      )

      if (!roleAllowed) {
        this.selectedRole = null
      }
    },

    async saveItem() {
      if (!this.form.id) {
        this.form.username = this.normalizeUsername(this.form.username)
      }

      this.submitted = true

      if (!this.form.username) return
      if (this.form.username.length < settings.minUsernameLength || this.form.username.length > settings.maxUsernameLength) return

      if (this.defaultLocationRequired && !this.selectedLocation?.id) return
      if (this.defaultRoleRequired && !this.selectedRole?.id) return

      if (this.passwordIsRequired) {
        if (
            !this.form.password ||
            !this.form.password_repeat ||
            this.form.password !== this.form.password_repeat ||
            this.form.password.length > settings.maxPasswordLength ||
            this.form.password.length < settings.minPasswordLength ||
            this.passwordServerError
        ) {
          return
        }
      }

      if (this.usernameTaken) return

      this.disableSaveButton = true
      this.dataIsSending = true

      const payload = {
        user_account: {
          id: this.form.id ?? null,
          user_id: this.form.user_id ?? this.userId ?? null,
          username: this.form.username ? this.form.username.toLowerCase() : null,
          label: this.form.label || null,
          default_location_id: this.selectedLocation?.id || null,
          default_role: this.selectedRole?.id || null,
          can_switch_location: this.canSwitchLocationBool ? 1 : 0,
          can_switch_role: this.canSwitchRoleBool ? 1 : 0,
          status: this.form.status ?? 1,
        }
      }

      if (this.passwordFieldsEnabled && this.form.password) {
        payload.user_account.password = this.form.password
      }

      try {
        const isEdit = !!this.form.id
        const url = isEdit ? 'user-access/update-user-account' : 'user-access/create-user-account'
        const expectedStatus = isEdit ? 200 : 201

        const { status, data } = await httpClient.post(url, payload)

        if (status === expectedStatus && data?.success) {
          const id = this.form.id ?? data.id
          this.$emit('update-items', id)

          this.$toast.add({
            severity:'success',
            detail: isEdit ? this.$t('Data updated') : this.$t('Data saved'),
            life: settings.toastLife
          });

          this.close()
          return
        }

        if (data?.error?.detail === 'Username has already been taken.') {
          this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('usernameTaken'), life: settings.toastLife})
          this.usernameTaken = true
          return
        }

        if (data?.error?.detail) {
          if (this.handlePasswordValidationError(data.error.detail)) {
            return
          }

          this.showError(data.error.detail)
          return
        }

        this.showUnknownError()
      } catch (err) {
        const detail = err?.response?.data?.message
            || err?.response?.data?.error?.detail

        if (this.handlePasswordValidationError(detail)) {
          return
        }

        this.showError(err)
      } finally {
        this.dataIsSending = false
        this.disableSaveButton = false
      }
    },

    handlePasswordValidationError(detail) {
      if (!this.isPasswordValidationError(detail)) {
        return false
      }

      this.passwordServerError = detail
      this.$toast.add({
        severity: 'error',
        summary: this.$t('Error'),
        detail: this.translatePasswordValidationError(detail),
        life: settings.toastLife,
      })

      return true
    },

    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
}
</script>

<style scoped lang="scss">
.p-button-text {
  width: auto;
}
</style>